import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import { reviewsService, type ReviewItem, type ReviewSummary } from '../../../shared/services/reviews.service';

type Props = {
  providerId: string;
};

/**
 * Sidebar ratings card. Renders nothing at all when the provider has no
 * reviews yet — an empty "no ratings" panel was hurting conversion on
 * fresh shops still building social proof, so we suppress the whole
 * surface until a first real review lands. Once {@code summary.count > 0}
 * we mount the Rating / Reviews tabs as before.
 */
export function ProviderRatingsCard({ providerId }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'rating' | 'reviews'>('rating');

  const summary = useQuery({
    queryKey: ['reviews', providerId, 'summary'],
    queryFn: () => reviewsService.summary(providerId),
    enabled: !!providerId,
  });

  const reviews = useQuery({
    queryKey: ['reviews', providerId, 'list'],
    queryFn: () => reviewsService.list(providerId, 0, 10),
    enabled:
      !!providerId &&
      tab === 'reviews' &&
      (summary.data?.count ?? 0) > 0,
  });

  // Hide the entire card while the summary loads or when the provider has
  // no reviews — better than a discouraging empty state on a fresh shop.
  if (summary.isLoading) return null;
  if (!summary.data || summary.data.count === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 48,
          '& .MuiTab-root': { fontWeight: 700, minHeight: 48 },
        }}
      >
        <Tab value="rating" label={t('reviews.ratingTab')} />
        <Tab value="reviews" label={t('reviews.reviewsTab')} />
      </Tabs>

      <Box sx={{ p: 2.5 }}>
        {tab === 'rating' ? (
          <RatingBreakdown data={summary.data} />
        ) : reviews.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : reviews.data && reviews.data.items.length > 0 ? (
          <ReviewList items={reviews.data.items} />
        ) : null}
      </Box>
    </Paper>
  );
}

function RatingBreakdown({ data }: { data: ReviewSummary }) {
  const { t } = useTranslation();
  const stars = [5, 4, 3, 2, 1] as const;
  return (
    <Stack direction="row" spacing={2} alignItems="stretch">
      <Box
        sx={{
          width: 84,
          minHeight: 132,
          borderRadius: 2,
          bgcolor: '#1A1816',
          color: 'common.white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontWeight: 800,
          fontSize: '2rem',
        }}
      >
        {data.average.toFixed(1)}
        <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.85, mt: 0.5 }}>
          / 5
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Stack spacing={0.75}>
          {stars.map((star) => {
            const count = data.distribution[star - 1] ?? 0;
            const pct = data.count > 0 ? (count / data.count) * 100 : 0;
            return (
              <Stack key={star} direction="row" spacing={0.75} alignItems="center">
                <Typography variant="body2" sx={{ width: 14 }}>
                  {star}
                </Typography>
                <StarRoundedIcon sx={{ fontSize: '1rem', color: '#1A1816' }} />
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{ flex: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" sx={{ width: 18, textAlign: 'right' }}>
                  {count}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1.5 }}
        >
          {t('reviews.reviewCount', { count: data.count })}
        </Typography>
      </Box>
    </Stack>
  );
}

function ReviewList({ items }: { items: ReviewItem[] }) {
  return (
    <Stack spacing={2} divider={<Box sx={{ height: '1px', bgcolor: 'divider' }} />}>
      {items.map((r) => (
        <Box key={r.id}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700 }}>
              {r.authorFirstName} {r.authorLastInitial}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              · {new Date(r.createdAt).toLocaleDateString()}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={0.25}>
              {Array.from({ length: 5 }).map((_, i) =>
                i < Math.round(r.rating) ? (
                  <StarRoundedIcon key={i} sx={{ fontSize: '1rem', color: '#1A1816' }} />
                ) : (
                  <StarOutlineRoundedIcon
                    key={i}
                    sx={{ fontSize: '1rem', color: 'text.disabled' }}
                  />
                ),
              )}
            </Stack>
          </Stack>
          {r.comment && (
            <Typography variant="body2" color="text.secondary">
              {r.comment}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}

